import disableThemes from '@/api/classes/disableThemes.endpoint';
import getClassTheme from '@/api/classes/getClassTheme.endpoint';
import updateThemeAndActivities from '@/api/classes/updateThemeAndActivities.endpoint';
import useCustomHook from '@/hooks/useCustomHook';
import openAPIErrorNotification from '@/shared/APIErrorNotification/APIErrorNotification';
import { GlobalContext } from '@/shared/GlobalContext/GlobalContext';
import { NavigationContext } from '@/shared/NavigationContext/NavigationContext';
import { notification } from 'antd';
import { useEffect, useContext } from 'react';

export default function useEditThemes({
  classId: propClassId,
  theme,
  onFinish,
}) {
  const navigationContext = useContext(NavigationContext);
  const globalContext = useContext(GlobalContext);

  const classId =
    propClassId ||
    (navigationContext.state.classes?.length
      ? navigationContext.state.classes?.[0]?.id
      : globalContext.state.user?.teacher?.internalUseClassId);

  const { state, actions } = useCustomHook({
    defaultState: {
      loading: true,
      processing: false,
      customEnabledActivities: [],
    },
    actions: {
      loadClassTheme: async () => {
        try {
          const classTheme = await getClassTheme(classId, theme.id);
          actions.setState({
            loading: false,
            customEnabledActivities: classTheme.enabledActivities,
          });
        } catch (e) {
          openAPIErrorNotification({
            title: 'There was an error',
            description: 'There was a problem loading the theme',
            errorFeedback: e.feedbackData,
          });
        }
      },
      onCustomActivitiesSelected: (activities) => {
        actions.setState({ customEnabledActivities: activities });
      },
      save: async () => {
        actions.setState({
          processing: true,
        });
        try {
          let themeDeleted = false;
          if (state.customEnabledActivities.length) {
            await updateThemeAndActivities(
              classId,
              theme.id,
              state.customEnabledActivities
            );
          } else {
            themeDeleted = true;
            await disableThemes(classId, [theme.id]);
          }
          actions.setState({
            processing: false,
          });
          notification.success({
            message: themeDeleted ? 'Theme deleted' : 'Theme updated',
            top: 60,
          });
          onFinish(themeDeleted);
        } catch (e) {
          actions.setState({ processing: false });
          openAPIErrorNotification({
            title: 'There was an error',
            description: 'There was a problem editing the theme',
            errorFeedback: e.feedbackData,
          });
        }
      },
    },
  });

  useEffect(() => {
    if (globalContext.state.loaded && globalContext.state.user?.teacher) {
      actions.loadClassTheme();
    }
  }, [globalContext.state.loaded]);

  return { state, actions };
}
